import { useRef, useState, useCallback } from 'react'
import { haptic } from '@/lib/haptics'

interface UseSwipeActionOptions {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  threshold?: number
  maxDistance?: number
  disabled?: boolean
}

export function useSwipeAction({
  onSwipeLeft,
  onSwipeRight,
  threshold = 80,
  maxDistance = 120,
  disabled = false,
}: UseSwipeActionOptions) {
  const [translateX, setTranslateX] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const startX = useRef(0)
  const startY = useRef(0)
  const direction = useRef<'horizontal' | 'vertical' | null>(null)

  const onTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled) return
      const touch = e.touches[0]
      startX.current = touch.clientX
      startY.current = touch.clientY
      direction.current = null
      setIsSwiping(true)
    },
    [disabled]
  )

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || !isSwiping) return
      const touch = e.touches[0]
      const deltaX = touch.clientX - startX.current
      const deltaY = touch.clientY - startY.current

      // Lock direction after 10px of movement
      if (direction.current === null) {
        if (Math.abs(deltaX) > 10) {
          direction.current = 'horizontal'
        } else if (Math.abs(deltaY) > 10) {
          direction.current = 'vertical'
          setIsSwiping(false)
          setTranslateX(0)
          return
        } else {
          return
        }
      }

      if (direction.current !== 'horizontal') return

      // Prevent vertical scroll while swiping horizontally
      e.preventDefault()

      const clamped = Math.max(-maxDistance, Math.min(maxDistance, deltaX))
      setTranslateX(clamped)
    },
    [disabled, isSwiping, maxDistance]
  )

  const onTouchEnd = useCallback(() => {
    if (disabled) return
    setIsSwiping(false)

    if (translateX > threshold && onSwipeRight) {
      haptic('success')
      onSwipeRight()
    } else if (translateX < -threshold && onSwipeLeft) {
      // 파괴적 좌측 스와이프(삭제)는 경고 패턴으로 구분
      haptic('warning')
      onSwipeLeft()
    }

    setTranslateX(0)
    direction.current = null
  }, [disabled, translateX, threshold, onSwipeLeft, onSwipeRight])

  return {
    translateX,
    isSwiping,
    handlers: { onTouchStart, onTouchMove, onTouchEnd },
  }
}
